import { memo } from 'react';
import { Button } from 'src/modules/common/components/Button/Button';
import { I18n } from 'src/modules/common/components/I18n/I18n';
import { MandatoryFilter, MESSAGES, MOBILE_WIDTH, OperationMode } from 'src/modules/common/constants';
import { ResetIcon } from 'src/modules/common/icons/ResetIcon';
import { HomePageData } from 'src/modules/home/HomePageMapping';
import { Filter, FilterConfig } from 'src/utils/composeFilter';
import clsx from 'clsx';
import { useScreenSize } from 'src/modules/common/hooks/useScreenSize';
import { CheckboxButton } from 'src/modules/common/components/Checkbox/CheckboxButton.tsx';
import { compareOperatingMode } from 'src/modules/home/components/compareOperatingMode.ts';
import { AttributeAlias, AttributesAliases } from 'src/generated/dict.ts';
import { isPressureDependentSelected } from 'src/modules/home/isPressureDependentSelected.ts';

type Props = {
    homePageData: HomePageData;
    filter: Filter;
    userFilter: Filter;
    filterConfig: FilterConfig;
    onChangeHandler: (detail: { name: string; value: string }) => void;
    onResetHandler: () => void;
}

export const HomeFilters = memo(({
    filterConfig,
    homePageData,
    filter,
    userFilter,
    onChangeHandler,
    onResetHandler,
}: Props) => {
    const { width } = useScreenSize();

    return (
        <aside className="vss-home-filters">
            {filterConfig
                .filter((item) => {
                    const [name] = item;
                    if (name === OperationMode) {
                        return isPressureDependentSelected(filter) &&
                            Array.isArray(filter.Pattern) && filter.Pattern.length > 0;
                    }
                    return name !== MandatoryFilter.ValveType;
                })
                .map((item) => {
                    const [name] = item;
                    const values = homePageData[`raw_${name}` as keyof HomePageData];
                    const valuesAvailable = (homePageData[name as keyof HomePageData]?.values ?? [])
                        .map((v) => v?.value);
                    const valuesAutoSelected = filter[name] || [];
                    const valuesSelectedByUser = userFilter[name] || [];
                    let data = values.values;
                    if (name === 'Type') {
                        data = data.slice()
                            .sort((a, b) => b.value.localeCompare(a.value));
                    }

                    if (name === 'Fluid') {
                        data = data.slice()
                            .sort((a, b) => b.value.localeCompare(a.value));
                    }

                    if (name === OperationMode && valuesAvailable.length === 0) {
                        return '';
                    }

                    if (name === OperationMode) {
                        data = data.slice()
                            .sort(compareOperatingMode);
                    }

                    return (
                        <div
                            key={name}
                            className={clsx({
                                'vss-home-filters__radio-group': true,
                            })}
                        >
                            <h3 className="vss-home-filters__radio-legend">
                                <I18n messageId={AttributesAliases[name as AttributeAlias]}/>
                            </h3>

                            {data.map(({ label, value }, index) => {
                                const checked = valuesAutoSelected.includes(value);
                                const isSingleVariant = valuesAvailable.length === 1 &&
                                    !valuesSelectedByUser.includes(value);
                                const allowed = valuesSelectedByUser.includes(value) ||
                                    (!checked && valuesAvailable.includes(value));
                                const selectedByUser = valuesSelectedByUser.includes(value);
                                const isDisabled = () => {
                                    switch (true) {
                                        case name === 'Type':
                                            return valuesAutoSelected.length > 0 && !selectedByUser;
                                        case name === MandatoryFilter.Pattern:
                                            return !valuesAvailable.includes(value) ||
                                                (valuesAutoSelected.length > 0 && !selectedByUser);
                                        case name === OperationMode:
                                            return !valuesAvailable.includes(value) ||
                                                (valuesAutoSelected.length > 0 && !selectedByUser);
                                        default:
                                            return isSingleVariant || !allowed;
                                    }
                                };
                                const disabled = isDisabled();
                                if (name === OperationMode && !valuesAvailable.includes(value)) {
                                    return '';
                                }
                                return (
                                    <CheckboxButton
                                        key={`${name}_${value}`}
                                        id={`${name}_${index}`}
                                        onChange={() => onChangeHandler({ value, name })}
                                        checked={checked}
                                        disabled={disabled}
                                    >{label.substring(0, 1) === '@'
                                            ? <I18n messageId={label.replace('@', '')}/> : label}
                                    </CheckboxButton>
                                );
                            })}
                        </div>
                    );
                })}

            {width > MOBILE_WIDTH && (
                <Button kind="tertiary" onClick={onResetHandler}>
                    <I18n messageId={MESSAGES.Reset}/>

                    <div className="vss-home-filters__button-icon">
                        <ResetIcon/>
                    </div>
                </Button>
            )}
        </aside>
    );
});
